import styled, { useTheme } from "styled-components"
import { CarouselControls, RadioButton, SceneOptionsToggle } from "slate-ui-components"
import { useFullscreen } from "./helpers"
import { useIntl } from "react-intl"
import { useAppDispatch, useAppSelector } from "./store/hooks"
import { VisualizerSlice, optionToggle } from "./store/reducers/visualizer"
import { useMediaQuery } from "@react-hook/media-query"
import { useAnalytics } from "./AnalyticsProvider"
import {useFrameState} from "./FrameStateProvider"

const ExploreOptionsListStyle = styled.div<{ "data-visible": boolean }>`
	display: flex;
	gap: 20px;
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	justify-content: center;
	align-items: center;
	margin: 20px 3px; 

	transition: 0.3s ease-out;
	transition-property: opacity, transform;
	
	&[data-visible=false]{
		opacity: 0;
		transform: translateY(150%);
		pointer-events: none;
	}

	z-index: 1;
`

export function ExploreOptions()
{
	const intl = useIntl();
	const dispatch = useAppDispatch();
	const {record} = useAnalytics();

	const {allowFullscreen, isFullscreen, enterFullScreen, exitFullScreen} = useFrameState()

	const categories = useAppSelector(s => s.configuration.currentVisualizerConfig.sceneCategories);
	const activeSceneCategory = useAppSelector(s => s.configuration.sceneOptionGroup);
	const currentMode = useAppSelector(s => s.configuration.currentMode)
	const requestedMode = useAppSelector(s => s.configuration.requestedMode)

	return <>
		{categories.map((category) =>
		{
			if(category.allowedModes && (!category.allowedModes.includes(currentMode) || !category.allowedModes?.includes(requestedMode))) {
				return null;
			}
			return <SceneOptionsToggle
				active={activeSceneCategory == category.id}
				key={category.id} icon={category.icon}
				label={intl.formatMessage(category.displayName)}
				testId={category.id}
				onClick={() => {
					dispatch(VisualizerSlice.actions.setSceneOptionGroup(activeSceneCategory != category.id ? category.id : undefined))	
					record?.(["Configurator", "Explore"], {item: category.id})
				}}
			/>
		})}
		{allowFullscreen && 
			<SceneOptionsToggle 
				active={isFullscreen} 
				icon="Fullscreen" 
				label={intl.formatMessage({id: "fullscreen", defaultMessage: "Full Screen"})} 
				testId={"fullscreen"} 
				onClick={() => {
					record?.(["Configurator", "Explore"], {item: "fullscreen"})
					if (isFullscreen) {
						exitFullScreen();
					}
					else {
						enterFullScreen();
				 	}
				}}
			/>
		}
	</>
}

export function ExploreOptionsList(props: { visible: boolean})
{
	const intl = useIntl();
	const dispatch = useAppDispatch();
	const theme = useTheme();
	const isFullScreenLayout = useMediaQuery(theme.breakpoints.fullScreenStream);

	let activeSceneCategory = useAppSelector(s => s.configuration.currentVisualizerConfig.sceneCategories.find(c => c.id == s.configuration.sceneOptionGroup));
	const fullScreenCategory = useAppSelector(s => s.configuration.currentVisualizerConfig.sceneCategories.find(c => c.id == "cameras"))

	activeSceneCategory = isFullScreenLayout ? fullScreenCategory : activeSceneCategory;

	const group = useAppSelector(s => s.configuration.optionGroups[activeSceneCategory?.group ?? ""]);
	const selected = useAppSelector(s => s.configuration.selectedOptions);
	const currentMode = useAppSelector(s => s.configuration.currentMode)

	if (activeSceneCategory?.allowedModes && !activeSceneCategory?.allowedModes?.includes(currentMode))
		return null;

	if (activeSceneCategory?.id == "cameras" && group)
	{
		const options = Object.entries(group.options);
		const currentIndex = options.findIndex(i => i[0] == selected[activeSceneCategory.id][0])
		return <ExploreOptionsListStyle data-visible={props.visible}>
			<CarouselControls total={options.length} current={currentIndex} onChange={(index) => dispatch(optionToggle({group: activeSceneCategory?.id, option: options[index][0]}))}/>
		</ExploreOptionsListStyle>
	}
	
	return <ExploreOptionsListStyle data-visible={props.visible}>
		{group && Object.entries(group.options).map(([optionId, option], key) =>
		{
			if(option.hidden) {
				return null;
			}
			return <RadioButton key={key}
				label={intl.formatMessage(option.displayName)}
				active={selected[activeSceneCategory?.group ?? ""]?.includes(optionId)}
				disabled={option.disabled}
				testId={optionId}
				onClick={() => activeSceneCategory && dispatch(optionToggle({group: activeSceneCategory?.id, option: optionId}))}
			/>
		})}
		
	</ExploreOptionsListStyle>
}